import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import * as RadixSelect from '@radix-ui/react-select';
import { clsx } from 'clsx';
import * as React from 'react';
import { RequiredLabel } from './RequiredLabel';
export const CURRENCY_OPTIONS = [
    // Popular choices
    { code: 'USD', symbol: '$' },
    { code: 'EUR', symbol: '€' },
    { code: 'GBP', symbol: '£' },
    { code: 'JPY', symbol: '¥' },
    { code: 'CAD', symbol: 'CA$' },
    { code: 'AUD', symbol: 'A$' },
    { code: 'CHF', symbol: 'CHF' },
    { code: 'CNY', symbol: 'CN¥' },
    { code: 'INR', symbol: '₹' },
    { code: 'NZD', symbol: 'NZ$' },
    // Alphabetical
    { code: 'AED', symbol: 'د.إ' },
    { code: 'ARS', symbol: 'AR$' },
    { code: 'BGN', symbol: 'лв' },
    { code: 'BRL', symbol: 'R$' },
    { code: 'CLP', symbol: 'CLP$' },
    { code: 'COP', symbol: 'COP$' },
    { code: 'CZK', symbol: 'Kč' },
    { code: 'DKK', symbol: 'kr' },
    { code: 'EGP', symbol: 'E£' },
    { code: 'HKD', symbol: 'HK$' },
    { code: 'HRK', symbol: 'kn' },
    { code: 'HUF', symbol: 'Ft' },
    { code: 'IDR', symbol: 'Rp' },
    { code: 'ILS', symbol: '₪' },
    { code: 'ISK', symbol: 'kr' },
    { code: 'KRW', symbol: '₩' },
    { code: 'MAD', symbol: 'MAD' },
    { code: 'MXN', symbol: 'MX$' },
    { code: 'MYR', symbol: 'RM' },
    { code: 'NOK', symbol: 'kr' },
    { code: 'PEN', symbol: 'S/' },
    { code: 'PHP', symbol: '₱' },
    { code: 'PKR', symbol: '₨' },
    { code: 'PLN', symbol: 'zł' },
    { code: 'RON', symbol: 'lei' },
    { code: 'RUB', symbol: '₽' },
    { code: 'SAR', symbol: '﷼' },
    { code: 'SEK', symbol: 'kr' },
    { code: 'SGD', symbol: 'S$' },
    { code: 'THB', symbol: '฿' },
    { code: 'TRY', symbol: '₺' },
    { code: 'TWD', symbol: 'NT$' },
    { code: 'ZAR', symbol: 'R' },
    { code: 'Other', symbol: '$' },
];
/** Format a raw numeric string with comma separators. Non-numeric input is returned as-is. */
function formatWithCommas(raw) {
    const cleaned = String(raw).replace(/[^0-9.]/g, '');
    if (!cleaned)
        return '';
    const [integer, decimal] = cleaned.split('.');
    const formattedInteger = (integer ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimal !== undefined ? `${formattedInteger}.${decimal}` : formattedInteger;
}
/** Strip formatting characters to get the raw numeric string. */
function stripFormatting(formatted) {
    return formatted.replace(/,/g, '');
}
export const CurrencyInput = React.forwardRef(({ className, label, required = false, error, helperText, suppressErrorMessage = false, showRequiredText = false, placeholder, disabled, id, size, value, defaultValue, onChange, currencies = CURRENCY_OPTIONS, }, ref) => {
    const inputId = id || React.useId();
    const hasError = Boolean(error);
    const describedBy = [hasError ? `${inputId}-error` : null, helperText ? `${inputId}-helper` : null]
        .filter(Boolean)
        .join(' ') || undefined;
    const isControlled = value !== undefined;
    const [displayAmount, setDisplayAmount] = React.useState(() => {
        const initial = isControlled ? (value?.amount ?? '') : (defaultValue?.amount ?? '');
        return formatWithCommas(initial);
    });
    const [currency, setCurrency] = React.useState(() => {
        return ((isControlled ? value?.currency : defaultValue?.currency) ?? currencies[0]?.code ?? 'USD');
    });
    // Keep display in sync with controlled value
    React.useEffect(() => {
        if (isControlled) {
            setDisplayAmount(formatWithCommas(value?.amount ?? ''));
            if (value?.currency !== undefined)
                setCurrency(value.currency);
        }
    }, [isControlled, value?.amount, value?.currency]);
    const currencySymbol = currencies.find((c) => c.code === currency)?.symbol ?? '$';
    const handleAmountChange = (e) => {
        const raw = stripFormatting(e.target.value);
        setDisplayAmount(raw);
        onChange?.({ amount: raw, currency });
    };
    const handleAmountBlur = () => {
        const formatted = formatWithCommas(stripFormatting(displayAmount));
        setDisplayAmount(formatted);
    };
    const handleAmountFocus = () => {
        setDisplayAmount(stripFormatting(displayAmount));
    };
    return (_jsxs("div", { className: clsx('currency-input', className), children: [(label || helperText) && (_jsxs("div", { className: "currency-input__header", children: [label && (_jsx("label", { htmlFor: inputId, className: "currency-input__label", children: _jsx(RequiredLabel, { label: label, required: required, showRequiredText: showRequiredText || hasError }) })), helperText && (_jsx("span", { id: `${inputId}-helper`, className: "currency-input__helper", children: helperText }))] })), _jsxs("div", { className: clsx('currency-input__wrapper', hasError && 'currency-input__wrapper--error'), children: [_jsx("div", { className: "currency-input__currency", children: _jsxs(RadixSelect.Root, { value: currency, onValueChange: (next) => {
                                setCurrency(next);
                                onChange?.({ amount: stripFormatting(displayAmount), currency: next });
                            }, disabled: disabled, children: [_jsxs(RadixSelect.Trigger, { className: "currency-input__currency-trigger", "aria-label": "Currency", children: [_jsx(RadixSelect.Value, {}), _jsx(RadixSelect.Icon, { asChild: true, children: _jsx(ChevronDownIcon, { className: "currency-input__currency-chevron", "aria-hidden": "true" }) })] }), _jsx(RadixSelect.Portal, { children: _jsx(RadixSelect.Content, { className: "select-content select-content--sm", side: "bottom", align: "start", alignOffset: -12, sideOffset: 6, collisionPadding: 8, position: "popper", children: _jsx(RadixSelect.Viewport, { className: "select-viewport", children: currencies.map((c) => (_jsxs(RadixSelect.Item, { value: c.code, className: "select-item", children: [_jsx(RadixSelect.ItemText, { children: c.code }), _jsx(RadixSelect.ItemIndicator, { className: "select-item-indicator", children: _jsx(CheckIcon, {}) })] }, c.code))) }) }) })] }) }), _jsx("span", { className: "currency-input__prefix", "aria-hidden": "true", children: currencySymbol }), _jsx("input", { id: inputId, ref: ref, type: "text", inputMode: "numeric", disabled: disabled, placeholder: placeholder, className: clsx('currency-input__input', size === 'comfortable' && 'currency-input__input--comfortable'), "aria-invalid": hasError, "aria-describedby": describedBy, value: displayAmount, onChange: handleAmountChange, onBlur: handleAmountBlur, onFocus: handleAmountFocus })] }), error && !suppressErrorMessage && (_jsx("p", { id: `${inputId}-error`, className: "currency-input__error", children: error }))] }));
});
CurrencyInput.displayName = 'CurrencyInput';
//# sourceMappingURL=CurrencyInput.js.map